/**
 * Utilities for validating and handling embedded iframe content.
 * Maintains a domain allowlist to prevent XSS/clickjacking from untrusted sources.
 */

const ALLOWED_EMBED_DOMAINS = [
  // 3D model / photogrammetry providers
  "poly.cam",
  "sketchfab.com",
  "matterport.com",
  "my.matterport.com",

  // Video platforms (360/immersive)
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "player.vimeo.com",

  // Mapping / spatial
  "cesium.com",
  "ion.cesium.com",
  "potree.org",

  // General 3D viewers
  "clara.io",
  "p3d.in",
  "3dviewer.net"
];

/**
 * Check whether a URL is from an allowed embed provider.
 */
export function isAllowedEmbedDomain(url: string): boolean {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    return ALLOWED_EMBED_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

/**
 * Return the full list of allowed domains (useful for UI display / admin hints).
 */
export function getAllowedEmbedDomains(): readonly string[] {
  return ALLOWED_EMBED_DOMAINS;
}

/**
 * Users often paste a full <iframe> embed snippet instead of just the URL.
 * Extract the `src` attribute if present, otherwise return the input as-is.
 */
export function extractEmbedSrc(input: string): string {
  const trimmed = input.trim();
  const iframeMatch = trimmed.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  return iframeMatch ? iframeMatch[1] : trimmed;
}

export interface ResolvedEmbed {
  provider: string;
  mediaType: "embed/3d" | "embed/video";
  embedUrl: string;
  thumbnail: string;
  name?: string;
}

/**
 * Resolve a pasted page URL (Polycam, Sketchfab, YouTube) into the embed_url,
 * thumbnail and media_type needed to create an embed media item.
 * Returns null if the URL doesn't match a known provider.
 */
export async function resolveEmbedFromUrl(pageUrl: string): Promise<ResolvedEmbed | null> {
  let parsed: URL;
  try {
    parsed = new URL(pageUrl);
  } catch {
    return null;
  }

  const hostname = parsed.hostname.toLowerCase().replace(/^www\./, "");

  // Polycam: https://poly.cam/capture/{CAPTURE_ID}[/embed]
  if (hostname === "poly.cam") {
    const match = parsed.pathname.match(/\/capture\/([^/]+)/i);
    if (!match) return null;
    const captureId = match[1];
    return {
      provider: "polycam",
      mediaType: "embed/3d",
      embedUrl: `https://poly.cam/capture/${captureId}/embed`,
      thumbnail: `https://storage.polycam.io/captures/${captureId}/thumbnail.jpg`,
      name: `Polycam capture ${captureId}` // Polycam has no oEmbed endpoint; user can edit after upload
    };
  }

  // Sketchfab: https://sketchfab.com/3d-models/{slug}-{MODEL_ID} or /models/{MODEL_ID}/embed
  if (hostname === "sketchfab.com") {
    const match = parsed.pathname.match(/([0-9a-f]{32})/i);
    if (!match) return null;
    const modelId = match[1];

    let thumbnail = "";
    let name: string | undefined;
    try {
      const oembedRes = await fetch(`https://sketchfab.com/oembed?url=${encodeURIComponent(pageUrl)}`);
      if (oembedRes.ok) {
        const data = await oembedRes.json();
        thumbnail = data?.thumbnail_url || "";
        name = data?.title || undefined;
      }
    } catch {
      // thumbnail/name stay empty if oEmbed lookup fails
    }

    return {
      provider: "sketchfab",
      mediaType: "embed/3d",
      embedUrl: `https://sketchfab.com/models/${modelId}/embed`,
      thumbnail,
      name
    };
  }

  // YouTube: https://youtu.be/{ID}, https://www.youtube.com/watch?v={ID} or /embed/{ID}
  if (hostname === "youtube.com" || hostname === "youtu.be") {
    let videoId = "";
    if (hostname === "youtu.be") {
      videoId = parsed.pathname.slice(1);
    } else if (parsed.pathname.startsWith("/embed/")) {
      videoId = parsed.pathname.replace("/embed/", "");
    } else {
      videoId = parsed.searchParams.get("v") || "";
    }
    if (!videoId) return null;

    let name: string | undefined;
    try {
      const oembedRes = await fetch(
        `https://www.youtube.com/oembed?url=${encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`)}&format=json`
      );
      if (oembedRes.ok) {
        const data = await oembedRes.json();
        name = data?.title || undefined;
      }
    } catch {
      // name stays empty if oEmbed lookup fails
    }

    return {
      provider: "youtube",
      mediaType: "embed/video",
      embedUrl: `https://www.youtube.com/embed/${videoId}`,
      thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      name
    };
  }

  return null;
}
